import * as Sentry from "@sentry/react";
import React, { useMemo, useState } from "react";

// icons
import { faCircleXmark, faInfoCircle, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";

import { AvatarProfile, ButtonStandard, ModalBase, PageBanner, Text } from "@performyard/py-components";

// components
import { AdminContainer, AdminTitle } from "../../../components/Admin";
import PageTitle from "../../../components/PageTitle";
import Spacer from "../../../components/Spacer";
import CompensationCycleOwners from "./components/CompensationCycleOwners";
import CompensationMetaCard from "./components/CompensationMetaCard";

import {
	useCompanySalaryCustomFieldData,
	useDismissSalaryFieldVisibilityConflictBanner,
	useRbacPrimaryRolePermissions,
	useUserBannerDismissalData,
	useUsersWithoutCompensationData,
} from "../../../api/hooks";
// hooks and queries
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import { compensationSettingsQuery, currentUserQuery } from "../../../api/queries";
import { dataLoader } from "../../../api/queries/dataLoader";

// utils
import { employeeDetailsLinkWithFrom } from "../../../utils/employeeDetailsBreadcrumbs";

// pages
import ErrorPage from "../../Error";

export const compensationSettingsLoader = () => {
	return dataLoader([[currentUserQuery], [compensationSettingsQuery]]);
};

type SalaryManagerVisibilityConflict = { kind: "specific"; audienceLabel: string } | { kind: "generic" };

/* 
	This function is to determine if the company's Salary Custom Field Visibility settings
	conflict with the inherent manager visibility of Compensation. If the visibility
	settings do not include "manager" - we want to alert the user of this conflict.
*/
const getSalaryManagerVisibilityConflict = (
	visibility: string[],
	isLoadingSalaryField: boolean
): SalaryManagerVisibilityConflict | null => {
	if (!isLoadingSalaryField && !visibility?.length) {
		return { kind: "generic" };
	}

	if (isLoadingSalaryField || visibility?.includes("manager")) {
		return null;
	}
	const isAdminsOnly = visibility.length === 1 && visibility.includes("admin");
	const isAdminsAndEmployee = visibility.length === 2 && visibility.includes("admin") && visibility.includes("self");
	if (isAdminsOnly) {
		return { kind: "specific", audienceLabel: "Admins only" };
	}
	if (isAdminsAndEmployee) {
		return { kind: "specific", audienceLabel: "Employee and Admins only" };
	}
	return { kind: "generic" };
};

const sortUsersByName = <T extends { name?: string | null }>(users: T[]): T[] =>
	[...users].sort((a, b) =>
		String(a?.name ?? "").localeCompare(String(b?.name ?? ""), undefined, { sensitivity: "base" })
	);

const Compensation = () => {
	// get data
	const { value: compensationEnabled } = useFeatureFlagQuery("ff-compensation", false);
	const { data: permissions, isLoading: isCompensationPermissionsLoading } =
		useRbacPrimaryRolePermissions("compensation");
	const { data: usersWithoutCompensationData } = useUsersWithoutCompensationData();
	const { data: salaryFieldData, isLoading: isLoadingSalaryField } = useCompanySalaryCustomFieldData();
	const { data: userBannerDismissalData } = useUserBannerDismissalData();
	const { mutate: dismiss } = useDismissSalaryFieldVisibilityConflictBanner();
	// set states
	const [activeModal, setActiveModal] = useState<"salary" | "manager" | null>(null);
	const [localDismissed, setLocalDismissed] = useState(false);
	const isSalaryVisibilityBannerDismissed = localDismissed || userBannerDismissalData?.userHasDismissedBanner;

	// parse data
	const excludedUsersWithoutManager = usersWithoutCompensationData?.excludeUsersWithoutManager || [];
	const excludedUsersWithoutSalary = usersWithoutCompensationData?.excludeUsersWithoutSalary || [];
	const salaryFieldVisibility = salaryFieldData?.salaryCustomField?.visibility ?? [];
	const { salaryManagerVisibilityConflict, salaryFieldConflictSubtitle } = useMemo(() => {
		const conflict = getSalaryManagerVisibilityConflict(salaryFieldVisibility, isLoadingSalaryField);
		const subtitle =
			conflict === null
				? null
				: conflict.kind === "specific"
					? `Your salary field is currently visible to '${conflict.audienceLabel}'. Please note that compensation cycles will allow selected managers to view salary data.`
					: "Your salary field is not visible to managers. Please note that compensation cycles will allow selected managers to view salary data.";
		return { salaryManagerVisibilityConflict: conflict, salaryFieldConflictSubtitle: subtitle };
	}, [salaryFieldVisibility, isLoadingSalaryField]);

	const { excludedUsersWithoutSalarySorted, excludedUsersWithoutManagerSorted } = useMemo(
		() => ({
			excludedUsersWithoutSalarySorted: sortUsersByName(excludedUsersWithoutSalary),
			excludedUsersWithoutManagerSorted: sortUsersByName(excludedUsersWithoutManager),
		}),
		[excludedUsersWithoutSalary, excludedUsersWithoutManager]
	);

	const modalConfig = {
		salary: {
			title: "Missing Salary Data",
			label: "salary data",
			users: excludedUsersWithoutSalarySorted,
		},
		manager: {
			title: "Missing a Manager",
			label: "a manager",
			users: excludedUsersWithoutManagerSorted,
		},
	} as const;

	const canViewCompensationSettings = isCompensationPermissionsLoading
		? undefined
		: permissions?.includes("compensation:viewSettings");

	if (canViewCompensationSettings === false) {
		return <ErrorPage code={403} />;
	}

	if (!compensationEnabled) {
		return <ErrorPage code={404} />;
	}

	return (
		<Sentry.ErrorBoundary fallback={<ErrorPage code={404} />}>
			<Spacer height={4} />
			<AdminContainer>
				<PageTitle title="Compensation Management" />
				<AdminTitle>Compensation Management</AdminTitle>
				<Spacer height={3} />
				<div className="flex flex-col gap-10">
					<div className="flex flex-col gap-4">
						<div className="flex justify-between gap-4">
							<div className="flex max-w-[364px] flex-col gap-2">
								<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
									Compensation Field
								</Text>
								<Text size="paragraph-3" weight="regular" color="neutral-9">
									This is the total salary for all employees in PerformYard. It is typically managed
									by your HRIS, but can also be managed manually.
								</Text>
							</div>
							<div className="w-full max-w-[546px]">
								<CompensationMetaCard />
							</div>
						</div>
						{(
							[
								{
									key: "salary" as const,
									users: excludedUsersWithoutSalarySorted,
									noun: "Salary Data",
									remedy: "a salary value is added",
								},
								{
									key: "manager" as const,
									users: excludedUsersWithoutManagerSorted,
									noun: "a Manager",
									remedy: "a manager is assigned",
								},
							] as const
						)
							.filter((b) => b.users.length > 0)
							.map(({ key, users, noun, remedy }) => (
								<div key={key} className="flex justify-between gap-4">
									<div className="max-w-[364px]" />
									<div className="w-full max-w-[546px]">
										<PageBanner
											color="red"
											icon={faTriangleExclamation}
											mainTitle={`${users.length} Employee${users.length === 1 ? " is" : "s"} Missing ${noun}`}
											subTitle={`These employees will be excluded from any compensation cycle until ${remedy}`}
											bottomActionButton={{
												variant: "text",
												children: (
													<Text size="paragraph-3" weight="semi-bold">
														View affected employees →
													</Text>
												),
												onClick: () => setActiveModal(key),
											}}
										/>
									</div>
								</div>
							))}
					</div>
					{activeModal && modalConfig[activeModal].users.length > 0 && (
						<ModalBase
							isOpen={!!activeModal}
							onClose={() => setActiveModal(null)}
							dangerouslySetClassName="!overflow-hidden w-[432px] max-h-[520px] min-h-0"
						>
							<div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
								<div className="flex shrink-0 flex-col items-center px-6 pb-4">
									<Text size="sub-heading" weight="semi-bold">
										{modalConfig[activeModal].title}
									</Text>
									<Text size="paragraph-2" weight="regular" color="neutral-7">
										{modalConfig[activeModal].users.length} Employee
										{modalConfig[activeModal].users.length === 1 ? "" : "s"} missing{" "}
										{modalConfig[activeModal].label}
									</Text>
								</div>
								<div className="mx-6 min-h-0 flex-1 [scrollbar-gutter:stable] overflow-x-hidden overflow-y-auto overscroll-contain">
									{modalConfig[activeModal].users.map((user, index) => (
										<div
											key={String(user?._id)}
											className={`flex h-[32px] items-center gap-3 pl-1 ${index % 2 === 0 ? "bg-neutral-1" : ""}`}
										>
											<AvatarProfile
												employeeId={String(user._id)}
												employeeName={user.name}
												avatarSize={20}
												textSize="paragraph-2"
												textWeight="regular"
												textColor="primary-8"
												href={employeeDetailsLinkWithFrom(user._id.toString())}
												isExternal={true}
											/>
										</div>
									))}
								</div>
								<div className="flex shrink-0 justify-center px-6 pt-4 pb-6">
									<ButtonStandard
										color="green"
										size="large"
										dangerouslySetClassName="w-auto"
										onClick={() => setActiveModal(null)}
									>
										Close
									</ButtonStandard>
								</div>
							</div>
						</ModalBase>
					)}
					<div className="flex justify-between gap-4 border-t border-solid border-neutral-2 pt-10">
						<div className="flex max-w-[364px] flex-col gap-2">
							<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
								Compensation Cycle Owners
							</Text>
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								Cycle owners have full access and editing capabilities for all information within
								compensation cycles.
							</Text>
						</div>

						<div className="flex w-full max-w-[546px] flex-col gap-4">
							{salaryManagerVisibilityConflict !== null &&
								isSalaryVisibilityBannerDismissed === false && (
									<div className="flex justify-between gap-4">
										<div className="w-full max-w-[546px]">
											<PageBanner
												color="primary"
												icon={faInfoCircle}
												mainTitle="Salary field visibility setting conflict"
												subTitle={salaryFieldConflictSubtitle}
												rightActionButton={{
													variant: "text",
													iconLeft: faCircleXmark,
													children: null,
													onClick: () => {
														dismiss();
														setLocalDismissed(true);
													},
												}}
											/>
										</div>
									</div>
								)}
							<CompensationCycleOwners />
						</div>
					</div>
				</div>
			</AdminContainer>
		</Sentry.ErrorBoundary>
	);
};

export default Compensation;
